import { useEffect, useMemo, useRef, useState } from 'react';
import { useGeolocated } from './useGeoLocated';

export const getAddressFromLatLng = async (
	position: google.maps.LatLngLiteral
): Promise<string> => {
	const geocoder = new google.maps.Geocoder();
	try {
		const response = await geocoder.geocode({ location: position });
		if (response.results.length === 0) {
			console.error('Geocoder returned no results for:', position);
			return 'Address not found';
		}
		return response.results[0].formatted_address;
	} catch (error) {
		console.error('Error in geocoding:', error);
		return 'Error in fetching address';
	}
};

export function useCurrentAddress() {
	const [address, setAddress] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const { coords } = useGeolocated();

	const position = useMemo(
		() => ({
			lat: coords?.latitude || 0,
			lng: coords?.longitude || 0,
		}),
		[coords]
	);

	useEffect(() => {
		if (coords && position.lat !== 0 && position.lng !== 0) {
			setLoading(true);
			getAddressFromLatLng(position)
				.then((addr) => setAddress(addr))
				.catch((err) => {
					console.error('Error fetching address:', err);
					setAddress(null);
				})
				.finally(() => setLoading(false));
		}
	}, [coords, position]);

	return { address, loading };
}

export interface PlaceSearchResult {
	address: string;
	position: google.maps.LatLngLiteral;
	placeId: string;
	geometry: {
		location: {
			lat: number;
			lng: number;
		};
		viewport?: {
			north: number;
			south: number;
			east: number;
			west: number;
		};
	};
}

export function usePlacesAutocomplete(initialValue = '') {
	const [inputValue, setInputValue] = useState(initialValue);
	const [suggestions, setSuggestions] = useState<PlaceSearchResult[]>([]);
	const [loading, setLoading] = useState(false);
	const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
	const placesService = useRef<google.maps.places.PlacesService | null>(null);

	useEffect(() => {
		if (window.google) {
			autocompleteService.current = new google.maps.places.AutocompleteService();
			const dummyElement = document.createElement('div');
			placesService.current = new google.maps.places.PlacesService(dummyElement);
		}
	}, []);

	const getPlaceDetails = async (placeId: string): Promise<PlaceSearchResult> => {
		return new Promise((resolve, reject) => {
			if (!placesService.current) {
				reject(new Error('Places service not initialized'));
				return;
			}

			placesService.current.getDetails(
				{
					placeId,
					fields: ['formatted_address', 'geometry', 'place_id'],
				},
				(result, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK && result) {
						resolve({
							address: result.formatted_address || '',
							position: {
								lat: result.geometry?.location?.lat() || 0,
								lng: result.geometry?.location?.lng() || 0,
							},
							geometry: {
								location: {
									lat: result.geometry?.location?.lat() || 0,
									lng: result.geometry?.location?.lng() || 0,
								},
								viewport: result.geometry?.viewport
									? {
											north: result.geometry.viewport.getNorthEast().lat(),
											south: result.geometry.viewport.getSouthWest().lat(),
											east: result.geometry.viewport.getNorthEast().lng(),
											west: result.geometry.viewport.getSouthWest().lng(),
										}
									: undefined,
							},
							placeId: result.place_id || '',
						});
					} else {
						reject(new Error('Failed to get place details'));
					}
				}
			);
		});
	};

	const searchPlaces = async (searchText: string) => {
		if (!searchText || !autocompleteService.current) return;

		setLoading(true);
		try {
			const response = await new Promise<google.maps.places.AutocompletePrediction[]>(
				(resolve, reject) => {
					autocompleteService.current?.getPlacePredictions(
						{ input: searchText },
						(predictions, status) => {
							if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
								resolve(predictions);
							} else {
								console.error('Autocomplete service failed with status:', status);
								reject(status);
							}
						}
					);
				}
			);

			const detailedResults = await Promise.all(
				response.map((prediction) => getPlaceDetails(prediction.place_id))
			);

			setSuggestions(detailedResults);
		} catch (error) {
			console.error('Error fetching place suggestions:', error);
			setSuggestions([]);
		} finally {
			setLoading(false);
		}
	};

	const handleInputChange = (value: string) => {
		setInputValue(value);
		if (value.length >= 3) {
			searchPlaces(value);
		} else {
			setSuggestions([]);
		}
	};

	return {
		inputValue,
		suggestions,
		loading,
		handleInputChange,
		setInputValue,
		setSuggestions,
	};
}

export async function getViewportFromLatLng(
	position: google.maps.LatLngLiteral
): Promise<google.maps.LatLngBounds> {
	const geocoder = new google.maps.Geocoder();
	try {
		const response = await geocoder.geocode({ location: position });
		if (response.results[0]?.geometry?.viewport) {
			return response.results[0].geometry.viewport;
		} else {
			throw new Error('No viewport found for this location');
		}
	} catch (error) {
		console.error('Error fetching viewport:', error);
		throw error;
	}
}
